import { ImageButton } from '@/ui/editor/ImageButton';
import { RiListOrdered, RiListUnordered } from 'react-icons/ri';
import { Icon } from '@chakra-ui/react';
// import { EditorColorPalette } from './EditorColorPalette';
import { EditorLink } from './EditorLink';
import { EditorFontSize } from './EditorFontSize';
import { EditorFontFamily } from './EditorFontFamily';
import { EditorSurvey } from './survey/EditorSurvey';
import { FileAttachment } from './FileAttachment';
import { colors } from '@/theme/foundations/colors';
import { DEFAULT_ATTACHMENT_FILES } from '@/constants/attachment-files';
import { InsertTable } from '@/assets/icons/table';
import {
  AlignLeft,
  AlignRight,
  Bold01,
  Code02,
  Eraser,
  HeadingLevel01,
  HeadingLevel02,
  HeadingLevel03,
  HeadingLevel04,
  Italic01,
  Pilcrow01,
  Strikethrough03,
} from '@kula-ai/icons-core';

const DEFAULT_ICON_COLOR = colors['secondary-700'];
const ACTIVE_ICON_COLOR = colors['secondary-700'];

const defaultDisableConfig = {
  attachments: {
    disabled: false,
    disabledMessage: 'Attachments are not supported here',
  },
  images: {
    disabled: false,
    disabledMessage: 'Images are not supported here',
  },
};

export const useEditorToolbarConfig = ({
  editor,
  isTextSelected,
  pluginsConfig,
  imageConfig,
  setImageConfig,
  attachmentKey = 'attachments',
  attachmentFiles = DEFAULT_ATTACHMENT_FILES.join(','),
  disableConfig = defaultDisableConfig,
  isMaxImageCountExceeded,
  hasCodeBlock = false,
  onAttachmentChange,
  onAttachmentRemove,
  attachmentTooltipMessage,
  totalSizeLimitMB,
  onSurveySelect,
  onSurveyRemove,
  enableFontSize = false,
  enableFontFamily = false,
}) => {
  const isAlignmentActive = (alignment: 'left' | 'right' | 'center') => {
    return (
      editor?.isActive('tableCell', { textAlign: alignment }) ||
      editor?.isActive('tableHeader', { textAlign: alignment })
    );
  };

  const handleTextAlign = (
    alignment: 'left' | 'right' | 'center',
    fallbackDirection?: 'ltr' | 'rtl'
  ) => {
    if (editor?.isActive('tableCell') || editor?.isActive('tableHeader')) {
      editor
        ?.chain()
        .focus()
        .updateAttributes('tableCell', { textAlign: alignment })
        .run();
      editor
        ?.chain()
        .focus()
        .updateAttributes('tableHeader', { textAlign: alignment })
        .run();
    } else if (fallbackDirection) {
      editor?.commands?.setTextDirection?.(fallbackDirection);
    }
  };

  const defaultEditorConfig = [
    {
      id: 'bold',
      title: 'Bold',
      icon: (
        // @ts-expect-error react-icons element typing under React 18
        <Bold01
          color={
            editor?.isActive('bold') ? ACTIVE_ICON_COLOR : DEFAULT_ICON_COLOR
          }
          size="16px"
        />
      ),
      onClick: () => editor?.chain().focus().toggleBold().run(),
      isTextSelected,
      isActive: editor?.isActive('bold'),
    },
    {
      id: 'italic',
      title: 'Italic',
      icon: (
        // @ts-expect-error react-icons element typing under React 18
        <Italic01
          color={
            editor?.isActive('italic') ? ACTIVE_ICON_COLOR : DEFAULT_ICON_COLOR
          }
          size="16px"
        />
      ),
      onClick: () => editor?.chain().focus().toggleItalic().run(),
      isTextSelected,
      isActive: editor?.isActive('italic'),
    },
    {
      id: 'removeFormat',
      title: 'Remove Format',
      icon: (
        // @ts-expect-error react-icons element typing under React 18
        <Eraser color={DEFAULT_ICON_COLOR} size="16px" />
      ),
      onClick: () => {
        if (editor) {
          editor.chain().focus().unsetAllMarks().clearNodes().run();
        }
      },
      isTextSelected,
      isActive: editor?.isActive('removeFormat'),
    },
    {
      id: 'strike',
      title: 'Strike',
      icon: (
        // @ts-expect-error react-icons element typing under React 18
        <Strikethrough03
          color={
            editor?.isActive('strike') ? ACTIVE_ICON_COLOR : DEFAULT_ICON_COLOR
          }
          size="16px"
        />
      ),
      onClick: () => editor?.chain().focus().toggleStrike().run(),
      isTextSelected,
      isActive: editor?.isActive('strike'),
    },
    {
      id: 'paragraph',
      title: 'Paragraph',
      icon: (
        // @ts-expect-error react-icons element typing under React 18
        <Pilcrow01
          color={
            editor?.isActive('paragraph')
              ? ACTIVE_ICON_COLOR
              : DEFAULT_ICON_COLOR
          }
          size="16px"
        />
      ),
      onClick: () => editor?.chain().focus().setParagraph().run(),
      isActive: editor?.isActive('paragraph'),
    },
    {
      id: 'heading1',
      title: 'Heading 1',
      icon: (
        // @ts-expect-error react-icons element typing under React 18
        <HeadingLevel01
          color={
            editor?.isActive('heading', { level: 1 })
              ? ACTIVE_ICON_COLOR
              : DEFAULT_ICON_COLOR
          }
          size="16px"
        />
      ),
      onClick: () => editor?.chain().focus().toggleHeading({ level: 1 }).run(),
      isActive: editor?.isActive('heading', { level: 1 }),
    },
    {
      id: 'heading2',
      title: 'Heading 2',
      icon: (
        // @ts-expect-error react-icons element typing under React 18
        <HeadingLevel02
          color={
            editor?.isActive('heading', { level: 2 })
              ? ACTIVE_ICON_COLOR
              : DEFAULT_ICON_COLOR
          }
          size="16px"
        />
      ),
      onClick: () => editor?.chain().focus().toggleHeading({ level: 2 }).run(),
      isActive: editor?.isActive('heading', { level: 2 }),
    },
    {
      id: 'heading3',
      title: 'Heading 3',
      icon: (
        // @ts-expect-error react-icons element typing under React 18
        <HeadingLevel03
          color={
            editor?.isActive('heading', { level: 3 })
              ? ACTIVE_ICON_COLOR
              : DEFAULT_ICON_COLOR
          }
          size="16px"
        />
      ),
      onClick: () => editor?.chain().focus().toggleHeading({ level: 3 }).run(),
      isActive: editor?.isActive('heading', { level: 3 }),
    },
    {
      id: 'heading4',
      title: 'Heading 4',
      icon: (
        // @ts-expect-error react-icons element typing under React 18
        <HeadingLevel04
          color={
            editor?.isActive('heading', { level: 4 })
              ? ACTIVE_ICON_COLOR
              : DEFAULT_ICON_COLOR
          }
          size="16px"
        />
      ),
      onClick: () => editor?.chain().focus().toggleHeading({ level: 4 }).run(),
      isActive: editor?.isActive('heading', { level: 4 }),
    },
    {
      id: 'alignLeft',
      title: 'Align Left',
      icon: (
        // @ts-expect-error react-icons element typing under React 18
        <AlignLeft
          color={
            isAlignmentActive('left') ? ACTIVE_ICON_COLOR : DEFAULT_ICON_COLOR
          }
          size="16px"
        />
      ),
      onClick: () => handleTextAlign('left', 'ltr'),
      isActive: isAlignmentActive('left'),
    },
    {
      id: 'alignRight',
      title: 'Align Right',
      icon: (
        // @ts-expect-error react-icons element typing under React 18
        <AlignRight
          color={
            isAlignmentActive('right') ? ACTIVE_ICON_COLOR : DEFAULT_ICON_COLOR
          }
          size="16px"
        />
      ),
      onClick: () => handleTextAlign('right', 'rtl'),
      isActive: isAlignmentActive('right'),
    },
    {
      id: 'unorderedList',
      title: 'Unordered List',
      icon: (
        // @ts-expect-error react-icons element typing under React 18
        <RiListUnordered
          color={
            editor?.isActive('bulletList')
              ? ACTIVE_ICON_COLOR
              : DEFAULT_ICON_COLOR
          }
          size="16px"
        />
      ),
      onClick: () => editor?.chain().focus().toggleBulletList().run(),
      isTextSelected,
      isActive: editor?.isActive('bulletList'),
    },
    {
      id: 'orderedList',
      title: 'Ordered List',
      icon: (
        // @ts-expect-error react-icons element typing under React 18
        <RiListOrdered
          color={
            editor?.isActive('orderedList')
              ? ACTIVE_ICON_COLOR
              : DEFAULT_ICON_COLOR
          }
          size="16px"
        />
      ),
      onClick: () => editor?.chain().focus().toggleOrderedList().run(),
      isTextSelected,
      isActive: editor?.isActive('orderedList'),
    },
    hasCodeBlock && {
      id: 'codeBlock',
      title: 'Code Block',
      onClick: () => editor?.chain().focus().toggleCodeBlock().run(),
      icon: (
        // @ts-expect-error react-icons element typing under React 18
        <Code02
          color={
            editor?.isActive('codeBlock')
              ? ACTIVE_ICON_COLOR
              : DEFAULT_ICON_COLOR
          }
        />
      ),
      isTextSelected,
      isActive: editor?.isActive('codeBlock'),
    },
    // {
    //   title: 'Text Color',
    //   color: editor?.getAttributes('textStyle').color
    //     ? editor.getAttributes('textStyle').color // If text has a color applied, use it
    //     : DEFAULT_ICON_COLOR, // Otherwise, use the default icon color
    //   Component: EditorColorPalette, // The component we created
    //   isActive: !!editor?.getAttributes('textStyle').color, // Active if a color is applied
    // },
  ];

  const additionalPlugins = [];

  const getOtherPluginsConfigs = () => {
    if (enableFontFamily) {
      additionalPlugins.push({
        title: 'Font family',
        Component: EditorFontFamily,
      });
    }

    if (enableFontSize) {
      additionalPlugins.push({
        title: 'Font size',
        Component: EditorFontSize,
      });
    }

    if (pluginsConfig.includes('link')) {
      additionalPlugins.push(
        {
          title: 'Insert Link',
          Component: EditorLink,
          isActive: editor?.isActive('link'),
        }
        // todo: enable in future if asked
        // {
        //   id: 'unlink',
        //   title: 'Unlink',
        //   icon: <RiLinkUnlink size="16px" />,
        //   color: editor?.isActive('link') ? DEFAULT_ICON_COLOR : '#aaa',
        //   onClick: () => editor?.chain().focus().unsetLink().run(),
        // }
      );
    }

    if (pluginsConfig.includes('attachment')) {
      additionalPlugins.push({
        title: 'Insert attachment',
        Component: FileAttachment,
        props: {
          fileTypes: attachmentFiles,
          attachmentKey,
          isDisabled: disableConfig.attachments?.disabled,
          disabledMessage: disableConfig.attachments?.disabledMessage,
          onAttachmentChange,
          onAttachmentRemove,
          tooltipMessage: attachmentTooltipMessage,
          totalSizeLimitMB,
        },
      });
    }

    if (pluginsConfig.includes('image')) {
      additionalPlugins.push({
        title: 'Insert image',
        Component: ImageButton,
        props: {
          setImageLoading: (val) =>
            setImageConfig((prev) => ({ ...prev, loading: val })),
          isDisabled: disableConfig.images?.disabled || imageConfig?.disabled,
          disabledMessage: disableConfig.images?.disabledMessage,
          isMaxImageCountExceeded,
          iconColor: 'secondary-700',
        },
      });
    }

    if (pluginsConfig.includes('table')) {
      additionalPlugins.push({
        id: 'table',
        title: 'Insert table',
        icon: (
          <Icon
            as={InsertTable as any}
            color={DEFAULT_ICON_COLOR}
            size="16px"
          />
        ),
        onClick: () =>
          editor
            ?.chain()
            .focus()
            .insertTable({ rows: 4, cols: 4, withHeaderRow: true })
            .run(),
      });
    }

    if (pluginsConfig.includes('survey')) {
      additionalPlugins.push({
        title: 'Include Survey',
        Component: EditorSurvey,
        isActive: editor?.isActive('surveySelect'),
        props: {
          onSurveySelect,
          onSurveyRemove,
        },
      });
    }
  };

  getOtherPluginsConfigs();

  return { defaultEditorConfig, additionalPlugins };
};
